import { useState, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import ScheduledReportCard from './ScheduledReportCard';
import ScheduledReportsFilters from './ScheduledReportsFilters';
import { ScheduledReport, FilterFormValues, ScheduledReportsTabId } from '../types';
import { rowGridSx, detailsPageSpacingSx } from '../styles';
import ViewButton from '../../admin/CentralReports/components/ViewButton/ViewButton';
import { INITIAL_CARDS_COUNT } from '../utils/ViewScheduleReportUtil';
import { useOrganizationUsersQuery } from '../../admin/requests/queries';

interface ScheduledReportsListProps {
  reports: ScheduledReport[];
  isLoading: boolean;
  formContext: UseFormReturn<FilterFormValues>;
  search: string;
  onSearchChange: (value: string) => void;
  showFilters?: boolean;
  currentTabId?: ScheduledReportsTabId;
  allReports?: ScheduledReport[]; // All reports for filter options generation
}

function ScheduledReportsList({
  reports,
  isLoading,
  formContext,
  search = '',
  onSearchChange,
  showFilters = false,
  currentTabId,
  allReports,
}: Readonly<ScheduledReportsListProps>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { data: organizationUsers = [], isLoading: isLoadingUsers } = useOrganizationUsersQuery(true);

  // Use allReports for filter options or fall back to current reports
  const reportsForFilters = allReports || reports;

  // Extract report card rendering logic
  const reportsToRender = useMemo(
    () => reports.map((report) => <ScheduledReportCard key={report.id} report={report} currentTabId={currentTabId} />),
    [reports, currentTabId]
  );

  const hasMore = reports.length > INITIAL_CARDS_COUNT;
  const visibleReports = expanded ? reportsToRender : reportsToRender.slice(0, INITIAL_CARDS_COUNT);
  if (isLoading) {
    return (
      <Grid container justifyContent='center' py={4}>
        <CircularProgress data-testid='scheduled-reports-loading' />
      </Grid>
    );
  }

  return (
    <>
      {/* Filters */}
      {showFilters && formContext && (
        <Grid item xs={12} sx={detailsPageSpacingSx}>
          <ScheduledReportsFilters
            formContext={formContext}
            search={search}
            onSearchChange={onSearchChange}
            organizationUsers={organizationUsers}
            isLoadingUsers={isLoadingUsers}
            reports={reportsForFilters}
          />
        </Grid>
      )}

      {/* Reports Grid */}
      {reports.length === 0 ? (
        <Grid container justifyContent='center' py={4}>
          <Typography color='textSecondary' data-testid='scheduled-reports-empty'>
            {t('admin.scheduledReports.noReports')}
          </Typography>
        </Grid>
      ) : (
        <>
          <Grid item xs={12} sx={rowGridSx} data-testid='scheduled-reports-list'>
            {visibleReports}
          </Grid>

          {hasMore && <ViewButton expanded={expanded} setExpanded={setExpanded} />}
        </>
      )}
    </>
  );
}

ScheduledReportsList.defaultProps = {
  showFilters: false,
};

export default ScheduledReportsList;
